import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import ExportModal from '../../components/common/ExportModal';
import SearchableSelect, { SearchableMultiSelect } from '../../components/common/SearchableSelect';
import CollapsibleFilterPanel, { FilterField, filterInputProps } from '../../components/common/CollapsibleFilterPanel';
import { orderAPI, brandAPI, mediatorAPI, productAPI, adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { isRefundPending } from '../../utils/refundTimeline';
import {
  buildOrderWhatsAppMessage,
  shareWhatsAppToNumber,
  isActionRequiredOrder,
  STATUS_LABELS,
} from '../../utils/orderWhatsAppMessage';
import PaginationBar from '../../components/common/PaginationBar';
const PaginationComponent = ({ pagination, onPageChange }) => (
  <PaginationBar
    page={pagination.currentPage}
    totalPages={pagination.totalPages}
    totalElements={pagination.totalItems}
    pageSize={pagination.pageSize}
    pageBase={1}
    onPageChange={onPageChange}
    style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', background: '#ffffff' }}
  />
);

const AdminOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusQuery = searchParams.get('status') || '';
  const refundAlertQuery = (searchParams.get('refundAlert') || '').toUpperCase();
  const initialStatusFilter = useMemo(() => {
    if (!statusQuery) return [];
    return statusQuery.split(',').map((s) => s.trim()).filter(Boolean);
  }, [statusQuery]);
  const [orders, setOrders] = useState([]);
  const [actionRequiredOrders, setActionRequiredOrders] = useState([]);
  const [actionRequiredExpanded, setActionRequiredExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [statusChangeOrder, setStatusChangeOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState(null);  const [mediators, setMediators] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    orderId: null,
  });
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [orderStatuses] = useState([
    { value: 'ORDER_FORM_FILLED', label: 'Order Form Filled' },
    { value: 'REFUND_FORM_FILLED', label: 'Refund Form Filled' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'REJECT_AND_REFILL', label: 'Reject and Refill' },
    { value: 'ORDER_FORM_REFILL_REQUEST', label: 'Order Form Refill Request' },
    { value: 'REFUND_FORM_REFILL_REQUEST', label: 'Refund Form Refill Request' },
    { value: 'PAID', label: 'Paid' },
    { value: 'PAID_TO_MEDIATOR', label: 'Order Paid To Mediator' },
    { value: 'PAID_TO_BUYER', label: 'Order Paid To Buyer' }
  ]);
  // Search form state
  const [searchForm, setSearchForm] = useState({
    page: 0,
    size: 10,
    sortBy: 'creationDate',
    sortDirection: 'DESC',
    orderId: '',
    status: initialStatusFilter,
    userEmail: '',
    mediatorId: '',
    productId: '',
    brandId: '',
    startDate: '',
    endDate: '',
    refundAlert: refundAlertQuery || '',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  });
  const mediatorOptions = mediators.map((mediator) => ({ value: mediator.id, label: mediator.mediatorName || mediator.fullName || mediator.username || `Mediator ${mediator.id}` }));
  const brandOptions = brands.map((brand) => ({ value: brand.id, label: brand.brandName }));
  const productOptions = products.map((product) => ({ value: product.id, label: product.productName || product.productCode || `Product ${product.id}` }));
  const orderStatusOptions = orderStatuses.map((status) => ({ value: status.value, label: status.label }));

  const activeFilterCount = useMemo(() => {
    let count = ['orderId', 'userEmail', 'mediatorId', 'productId', 'brandId', 'startDate', 'endDate', 'refundAlert']
      .filter((k) => searchForm[k]).length;
    if (searchForm.status?.length) count += 1;
    return count;
  }, [searchForm]);

  const mapOrderRow = (order) => ({
    id: order.id,
    orderId: order.orderId,
    customer: order.reviewerName || order.userEmail,
    product: order.productName || `Product ${order.productId}`,
    amount: order.orderAmount,
    status: order.orderStatus,
    date: order.orderDate,
    userEmail: order.userEmail,
    mediatorId: order.mediatorId,
    mediatorName: order.mediatorName,
    productId: order.productId,
    productName: order.productName,
    brandId: order.brandId,
    brandName: order.brandName,
    lessAmount: order.lessAmount,
    dealFeedbackType: order.dealFeedbackType,
    dealOrderType: order.dealOrderType,
    whatsappNumber: order.whatsappNumber,
    mediatorPhoneNumber: order.mediatorPhoneNumber,
    refundTimeline: order.refundTimeline,
    refundFormFilledDate: order.refundFormFilledDate,
    penaltyAmount: order.penaltyAmount,
    reason: order.reason,
    paymentMethod: order.paymentMethod,
    accountHolderName: order.accountHolderName,
    bankAccount: order.bankAccount,
    bankName: order.bankName,
    ifscCode: order.ifscCode,
    upiId: order.upiId,
    orderScreenshot: order.orderScreenshot,
    deliveredOrderScreenshot: order.deliveredOrderScreenshot,
    returnWindowCloseScreenshot: order.returnWindowCloseScreenshot,
    reviewRatingScreenshot: order.reviewRatingScreenshot,
    sellerFeedbackScreenshot: order.sellerFeedbackScreenshot,
  });

  const fetchActionRequiredOrders = async () => {
    try {
      const [refillRes, refundPendingRes] = await Promise.all([
        orderAPI.searchOrders({
          page: 0,
          size: 100,
          status: ['REJECTED', 'REJECT_AND_REFILL', 'ORDER_FORM_REFILL_REQUEST', 'REFUND_FORM_REFILL_REQUEST'],
          sortBy: 'creationDate',
          sortDirection: 'DESC',
        }),
        orderAPI.searchOrders({
          page: 0,
          size: 100,
          status: ['ORDER_FORM_FILLED', 'ORDER_FORM_REFILL_REQUEST'],
          sortBy: 'creationDate',
          sortDirection: 'DESC',
        }),
      ]);
      const refillOrders = (refillRes.data?.data || []).map(mapOrderRow);
      const pendingRefund = (refundPendingRes.data?.data || []).map(mapOrderRow);
      const closingSoon = pendingRefund.filter(isActionRequiredOrder);
      const merged = [...refillOrders];
      closingSoon.forEach((order) => {
        if (!merged.some((o) => o.id === order.id)) merged.push(order);
      });
      setActionRequiredOrders(merged.filter(isActionRequiredOrder));
    } catch {
      setActionRequiredOrders([]);
    }
  };

  const buildSearchPayload = (page = 1, overrides = null) => {
    const source = overrides || searchForm;
    const params = {
      ...source,
      page: page - 1,
      size: source.size || 10,
      sortBy: source.sortBy || 'creationDate',
      sortDirection: source.sortDirection || 'DESC',
    };
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete params[key];
      }
    });
    return params;
  };

  const fetchOrders = async (page = 1, searchParamsOverride = null) => {
    setLoading(true);
    try {
      const params = buildSearchPayload(page, searchParamsOverride);
      const response = await orderAPI.searchOrders(params);
      const data = response.data;
      const mappedOrders = (data.data || []).map(mapOrderRow);
      setOrders(mappedOrders);
      setPagination({
        currentPage: page,
        totalPages: data.totalPages || 1,
        totalItems: data.totalElements || 0,
        pageSize: data.size || 10
      });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [mediatorsRes, productsRes, brandsRes] = await Promise.all([
          mediatorAPI.getAllMediators(),
            productAPI.getProducts({ size: 1000, sortBy: 'productName' }),

            // productAPI.getActiveProducts(),
          brandAPI.getActiveBrands()
          // brandAPI.getBrands({ size: 1000, sortBy: 'brandName' })
        ]);
        setMediators(mediatorsRes.data.data || []);
        // Check if response has data.data (for search/paged) or just data (for simple list)
        setProducts(productsRes.data.data || productsRes.data || []);
        setBrands(brandsRes.data.data || brandsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const params = {
      page: 0,
      size: 10,
      sortBy: 'creationDate',
      sortDirection: 'DESC',
      orderId: '',
      status: initialStatusFilter,
      userEmail: '',
      mediatorId: '',
      productId: '',
      brandId: '',
      startDate: '',
      endDate: '',
      refundAlert: refundAlertQuery || '',
    };
    setSearchForm(params);
    fetchOrders(1, params);
    fetchActionRequiredOrders();
  }, [statusQuery, refundAlertQuery]);

  const handleSelectOrder = (id) => {
    setSelectedOrderIds(prev =>
        prev.includes(id)
            ? prev.filter(orderId => orderId !== id)
            : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrderIds.length === orders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map(o => o.id));
    }
  };
  const bulkStatuses = orderStatuses.filter(s => 
    !['REJECTED', 'REJECT_AND_REFILL', 'ORDER_FORM_REFILL_REQUEST', 'REFUND_FORM_REFILL_REQUEST'].includes(s.value)
  );

  const confirmBulkMark = async () => {
    if (selectedOrderIds.length === 0 || !bulkTargetStatus) return;

    // Validation for payment statuses
    if (['PAID', 'PAID_TO_MEDIATOR', 'PAID_TO_BUYER'].includes(bulkTargetStatus)) {
      const invalidOrders = selectedOrderIds.filter(id => {
        const order = orders.find(o => o.id === id);
        return !order?.reviewLink || order.reviewLink.trim() === '';
      });

      if (invalidOrders.length > 0) {
        toast.error(`Cannot update status. ${invalidOrders.length} order(s) are missing refund details (Review Link). Please fill the refund details before marking as Paid.`);
        setShowBulkConfirm(false);
        return;
      }
    }

    setIsBulkMarking(true);
    try {
      let successCount = 0;

      for (const id of selectedOrderIds) {
        try {
          await adminAPI.updateOrderStatus(id, bulkTargetStatus);
          successCount++;
        } catch (err) {
          console.error(`Failed to update order ${id}`, err);
        }
      }

      toast.success(`Updated ${successCount} orders to ${getStatusBadge(bulkTargetStatus).label}`);
      setSelectedOrderIds([]);
      fetchOrders(pagination.currentPage);

    } catch (error) {
      toast.error('Bulk update failed');
    } finally {
      setIsBulkMarking(false);
      setShowBulkConfirm(false);
      setBulkTargetStatus(null);
    }
  };
  const handleBulkMarkClick = (status) => {
    setBulkTargetStatus(status);
    setShowBulkConfirm(true);
    setShowBulkStatusModal(false);
  };

  const openStatusChangeModal = (order) => {
    setStatusChangeOrder(order);
  };

  const closeStatusChangeModal = () => {
    setStatusChangeOrder(null);
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setSearchLoading(true);
    try {
      await fetchOrders(1, searchForm);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleReset = () => {
    const cleared = {
      page: 0, size: 10, sortBy: 'creationDate', sortDirection: 'DESC',
      orderId: '', status: [], userEmail: '', mediatorId: '', productId: '', brandId: '',
      startDate: '', endDate: '', refundAlert: '',
    };
    setSearchForm(cleared);
    fetchOrders(1, cleared);
  };

  const handleInputChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    if (searchForm.orderId) chips.push({ key: 'orderId', label: 'Order ID', value: searchForm.orderId, onClear: () => handleInputChange('orderId', '') });
    if (searchForm.status?.length) chips.push({ key: 'status', label: 'Status', value: `${searchForm.status.length} selected`, onClear: () => handleInputChange('status', []) });
    if (searchForm.userEmail) chips.push({ key: 'userEmail', label: 'Email', value: searchForm.userEmail, onClear: () => handleInputChange('userEmail', '') });
    if (searchForm.mediatorId) {
      const label = mediatorOptions.find((o) => o.value === searchForm.mediatorId)?.label || searchForm.mediatorId;
      chips.push({ key: 'mediatorId', label: 'Mediator', value: label, onClear: () => handleInputChange('mediatorId', '') });
    }
    if (searchForm.brandId) {
      const label = brandOptions.find((o) => o.value === searchForm.brandId)?.label || searchForm.brandId;
      chips.push({ key: 'brandId', label: 'Brand', value: label, onClear: () => handleInputChange('brandId', '') });
    }
    if (searchForm.productId) {
      const label = productOptions.find((o) => o.value === searchForm.productId)?.label || searchForm.productId;
      chips.push({ key: 'productId', label: 'Product', value: label, onClear: () => handleInputChange('productId', '') });
    }
    if (searchForm.startDate) chips.push({ key: 'startDate', label: 'From', value: searchForm.startDate, onClear: () => handleInputChange('startDate', '') });
    if (searchForm.endDate) chips.push({ key: 'endDate', label: 'To', value: searchForm.endDate, onClear: () => handleInputChange('endDate', '') });
    if (searchForm.refundAlert) {
      const labels = {
        CLOSING_SOON: 'Closing soon',
        URGENT: 'Urgent / penalty risk',
        OVERDUE: 'Overdue reminders',
      };
      chips.push({
        key: 'refundAlert',
        label: 'Refund alert',
        value: labels[searchForm.refundAlert] || searchForm.refundAlert,
        onClear: () => handleInputChange('refundAlert', ''),
      });
    }
    return chips;
  }, [searchForm, mediatorOptions, brandOptions, productOptions]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    closeStatusChangeModal();
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  const [editingOrder, setEditingOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setEditFormData({
      orderId: order.orderId,
      productId: order.productId,
      userEmail: order.userEmail,
      whatsappNumber: order.whatsappNumber,
      reviewerName: order.customer,
      orderDate: order.date,
      mediatorId: order.mediatorId,
      orderAmount: order.amount,
      dealFeedbackType: order.dealFeedbackType,
      dealOrderType: order.dealOrderType,
      lessAmount: order.lessAmount,
      orderStatus: order.status,
      reason: order.reason,
      paymentMethod: order.paymentMethod,
      bankName: order.bankName,
      bankAccount: order.bankAccount,
      ifscCode: order.ifscCode,
      upiId: order.upiId,
      accountHolderName: order.accountHolderName,
      returnPeriodOverDate: order.returnPeriodOverDate,
      reviewLink: order.reviewLink,
      orderScreenshot: order.orderScreenshot,
      sellerFeedbackScreenshot: order.sellerFeedbackScreenshot,
      returnWindowCloseScreenshot: order.returnWindowCloseScreenshot,
      reviewRatingScreenshot: order.reviewRatingScreenshot,
      deliveredOrderScreenshot: order.deliveredOrderScreenshot,
      oldOrderId: order.oldOrderId
    });
    setShowEditModal(true);
    closeStatusChangeModal();
  };
  const openDeleteModal = (orderId) => {
    setDeleteModal({ open: true, orderId });
  };

  const confirmDelete = async () => {
    try {
      const response = await orderAPI.deleteOrder(deleteModal.orderId);

      if (response.data.success) {
        toast.success('Order deleted successfully');
        fetchOrders(pagination.currentPage);
      } else {
        toast.error(response.data.message || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    } finally {
      setDeleteModal({ open: false, orderId: null });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const formData = new FormData();
      
      // Construct OrderFormDto based on current editFormData
      const orderFormDto = {
        orderId: editFormData.orderId,
        productId: editFormData.productId,
        userEmail: editFormData.userEmail,
        whatsappNumber: editFormData.whatsappNumber,
        reviewerName: editFormData.reviewerName,
        orderDate: editFormData.orderDate,
        mediatorId: editFormData.mediatorId,
        orderAmount: parseFloat(editFormData.orderAmount),
        dealFeedbackType: editFormData.dealFeedbackType,
        dealOrderType: editFormData.dealOrderType,
        lessAmount: parseFloat(editFormData.lessAmount || 0),
        orderStatus: editFormData.orderStatus,
        reason: editFormData.reason,
        paymentMethod: editFormData.paymentMethod,
        bankName: editFormData.bankName,
        bankAccount: editFormData.bankAccount,
        ifscCode: editFormData.ifscCode,
        upiId: editFormData.upiId,
        accountHolderName: editFormData.accountHolderName,
        returnPeriodOverDate: editFormData.returnPeriodOverDate,
        reviewLink: editFormData.reviewLink,
        oldOrderId: editFormData.oldOrderId
      };

      formData.append(
        'data',
        new Blob(
          [JSON.stringify(orderFormDto)],
          { type: 'application/json' }
        )
      );

      // Add screenshots if they exist as strings (URLs) or if we were to add file selection in future
      // For now, we are sending the DTO as multipart/form-data as required by the updated API
      
      const response = await orderAPI.updateOrderForm(editingOrder.id, formData);
      if (response.data.success) {
        toast.success('Order updated successfully');
        setShowEditModal(false);
        fetchOrders(pagination.currentPage);
      }
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error(error.response?.data?.message || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdateClick = (orderId, newStatus) => {
    if (['REJECTED', 'REJECT_AND_REFILL', 'ORDER_FORM_REFILL_REQUEST', 'REFUND_FORM_REFILL_REQUEST'].includes(newStatus)) {
      setPendingStatusUpdate({ orderId, newStatus });
      setReason('');
      setShowReasonModal(true);
      closeStatusChangeModal();
    } else {
      setStatusToUpdate({ orderId, newStatus });
      setShowStatusConfirm(true);
      closeStatusChangeModal();
    }
  };

  const confirmReasonUpdate = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required for this status');
      return;
    }

    const { orderId, orderIds, newStatus, isBulk } = pendingStatusUpdate;
    try {
      if (isBulk) {
        setIsBulkMarking(true);
        let successCount = 0;
        for (const id of orderIds) {
          try {
            await adminAPI.updateOrderStatus(id, newStatus, reason);
            successCount++;
          } catch (err) {
            console.error(`Failed to update order ${id}`, err);
          }
        }
        toast.success(`Updated ${successCount} orders to ${getStatusBadge(newStatus).label}`);
        setSelectedOrderIds([]);
        fetchOrders(pagination.currentPage);
        fetchActionRequiredOrders();
      } else {
        setUpdatingStatus(orderId);
        const response = await adminAPI.updateOrderStatus(orderId, newStatus, reason);
        if (response.data.success) {
          toast.success('Status updated successfully');
          setOrders(prevOrders =>
            prevOrders.map(order =>
              order.id === orderId ? { ...order, status: newStatus, reason } : order
            )
          );
          fetchActionRequiredOrders();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
      setIsBulkMarking(false);
      setShowReasonModal(false);
      setPendingStatusUpdate(null);
      setReason('');
    }
  };

  const confirmStatusUpdate = async () => {
    if (!statusToUpdate) return;

    const { orderId, newStatus } = statusToUpdate;
    try {
      setUpdatingStatus(orderId);
      const response = await adminAPI.updateOrderStatus(orderId, newStatus, '');
      if (response.data.success) {
        toast.success('Status updated successfully');
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId ? { ...order, status: newStatus } : order
          )
        );
        fetchActionRequiredOrders();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
      setShowStatusConfirm(false);
      setStatusToUpdate(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      ORDER_FORM_FILLED: { color: '#3b82f6', bg: '#dbeafe', label: 'Order Form Filled' },
      REFUND_FORM_FILLED: { color: '#f59e0b', bg: '#fef3c7', label: 'Refund Filled' },
      APPROVED: { color: '#10b981', bg: '#d1fae5', label: 'Approved' },
      REJECTED: { color: '#ef4444', bg: '#fee2e2', label: 'Rejected' },
      REJECT_AND_REFILL: { color: '#be185d', bg: '#fce7f3', label: 'Reject and Refill' },
      ORDER_FORM_REFILL_REQUEST: { color: '#ec4899', bg: '#fdf2f8', label: 'Order Refill' },
      REFUND_FORM_REFILL_REQUEST: { color: '#ec4899', bg: '#fdf2f8', label: 'Refund Refill' },
      PAID: { color: '#059669', bg: '#ecfdf5', label: 'Paid' },
      PAID_TO_MEDIATOR: { color: '#8b5cf6', bg: '#f3e8ff', label: 'Paid to Mediator' },
      PAID_TO_BUYER: { color: '#059669', bg: '#dcfce7', label: 'Paid to Buyer' }
    };
    return config[status] || { color: '#64748b', bg: '#f3f4f6', label: status };
  };

  const handleWhatsAppClick = (number, order = null) => {
    if (!number) {
      toast.error('Phone number not available');
      return;
    }
    const message = order ? buildOrderWhatsAppMessage(order) : '';
    shareWhatsAppToNumber(number, message);
  };

  const formsFilledCount = orders.filter((o) =>
    ['ORDER_FORM_FILLED', 'REFUND_FORM_FILLED', 'APPROVED', 'PAID', 'PAID_TO_MEDIATOR', 'PAID_TO_BUYER'].includes(o.status)
  ).length;
  const pendingRefundCount = orders.filter((o) => isRefundPending(o.status)).length;
  const notifyCount = orders.filter((o) =>
    isRefundPending(o.status) &&
    ['REMINDER', 'PENALTY_50', 'PENALTY_100', 'CLOSED'].includes(o.refundTimeline?.warningLevel)
  ).length;

  return (
    <div className="orders-page-shell" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <div className="orders-page-top page-section-top" style={{ padding: '16px 24px 12px 24px', backgroundColor: '#f8fafc', flexShrink: 0, position: 'relative', zIndex: 5, overflow: 'visible' }}>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Total Orders</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b' }}>{pagination.totalItems}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Forms Filled</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b' }}>{formsFilledCount}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Pending Refunds</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#d97706' }}>{pendingRefundCount}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600 }}>Need Notification</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#92400e' }}>{notifyCount}</div>
          </div>
        </div>
        {actionRequiredOrders.length > 0 && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '12px',
            background: '#fdf2f8',
            border: '1px solid #f9a8d4',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setActionRequiredExpanded((open) => !open)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 800,
                  color: '#9d174d',
                }}
              >
                <Icon name={actionRequiredExpanded ? 'chevron-down' : 'chevron-right'} size={16} />
                Action Required ({actionRequiredOrders.length})
              </button>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => navigate('/admin/action-required')}
                  style={{
                    padding: '6px 12px',
                    background: '#be185d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Open full page
                </button>
              </div>
            </div>
            {actionRequiredExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {actionRequiredOrders.map((order) => {
                  const caseLabel = STATUS_LABELS[order.status]
                    || (order.refundTimeline?.daysLeftToClose != null
                      ? `Refund closes in ${order.refundTimeline.daysLeftToClose} day(s)`
                      : 'Needs attention');
                  return (
                    <div
                      key={`action-${order.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: '#fff',
                        border: '1px solid #fbcfe8',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#831843', fontSize: '14px' }}>{order.orderId}</span>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: '#fce7f3',
                        color: '#9d174d',
                      }}>
                        {caseLabel}
                      </span>
                      {order.reason && (
                        <span style={{ fontSize: '13px', color: '#64748b', flex: 1 }}>
                          Reason: {order.reason}
                        </span>
                      )}
                      <button
                        onClick={() => handleWhatsAppClick(order.whatsappNumber, order)}
                        style={{
                          padding: '6px 12px',
                          background: '#25D366',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        WhatsApp Buyer
                      </button>
                      <button
                        onClick={() => handleViewOrder(order)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        View
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="page-toolbar admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>Order Management</h2>
          <div className="page-toolbar-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>


            {selectedOrderIds.length > 0 && (
                <Button
                    variant="primary"
                    onClick={() => setShowBulkStatusModal(true)}
                    loading={isBulkMarking}
                    style={{ background: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  Mark Selected ({selectedOrderIds.length})
                </Button>
            )}

            <Button variant="outline" onClick={() => setShowExportModal(true)}>
              <Icon name="download" size={16} /> Export Sheet
            </Button>

            <Button variant="secondary" onClick={() => fetchOrders(1, searchForm)}>
              <Icon name="refresh" size={16} /> Refresh
            </Button>

          </div>
        </div>
        <CollapsibleFilterPanel
          title="Search & Filters"
          defaultOpen={false}
          activeCount={activeFilterCount}
          activeFilters={activeFilters}
          onReset={handleReset}
          onSearch={handleSearch}
          searchLoading={searchLoading}
        >
          <FilterField label="Order ID">
            <input type="text" placeholder="e.g. ORD-12345" value={searchForm.orderId} onChange={(e) => handleInputChange('orderId', e.target.value)} {...filterInputProps} />
          </FilterField>
          <FilterField label="Status">
            <SearchableMultiSelect
              options={orderStatusOptions}
              selectedValues={searchForm.status}
              onChange={(value) => handleInputChange('status', value)}
              placeholder="All statuses"
            />
          </FilterField>
          <FilterField label="Buyer email">
            <input type="email" placeholder="buyer@email.com" value={searchForm.userEmail} onChange={(e) => handleInputChange('userEmail', e.target.value)} {...filterInputProps} />
          </FilterField>
          <FilterField label="Mediator">
            <SearchableSelect options={mediatorOptions} value={searchForm.mediatorId} onChange={(value) => handleInputChange('mediatorId', value)} placeholder="All mediators" searchPlaceholder="Search mediator..." />
          </FilterField>
          <FilterField label="Brand">
            <SearchableSelect options={brandOptions} value={searchForm.brandId} onChange={(value) => handleInputChange('brandId', value)} placeholder="All brands" searchPlaceholder="Search brand..." />
          </FilterField>
          <FilterField label="Product">
            <SearchableSelect options={productOptions} value={searchForm.productId} onChange={(value) => handleInputChange('productId', value)} placeholder="All products" searchPlaceholder="Search product..." />
          </FilterField>
          <FilterField label="From date">
            <input type="date" value={searchForm.startDate} onChange={(e) => handleInputChange('startDate', e.target.value)} {...filterInputProps} />
          </FilterField>
          <FilterField label="To date">
            <input type="date" value={searchForm.endDate} onChange={(e) => handleInputChange('endDate', e.target.value)} {...filterInputProps} />
          </FilterField>
        </CollapsibleFilterPanel>
      </div>
      <div className="orders-table-section" style={{ flex: 1, padding: '0 24px 24px 24px', overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', position: 'relative', zIndex: 1 }}>
        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
          <div className="table-scroll-container" style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '12px 16px' }}>
                    <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={orders.length > 0 && selectedOrderIds.length === orders.length}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#3b82f6'   // ✅ clean fix
                        }}
                    />
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Order ID</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Customer</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Mediator</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Penalty</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Refund Filled</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center' }}>Loading...</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center' }}>No orders found</td></tr>
                ) : (
                  orders.map(order => (
                      <tr
                          key={order.id}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: selectedOrderIds.includes(order.id)
                                ? '#f0f7ff'
                                : 'transparent'
                          }}
                      >
                      <td style={{ padding: '12px 16px' }}>
                        <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              accentColor: '#3b82f6'   // ✅ remove grey bg
                            }}
                        />
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>{order.orderId}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>{order.customer}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>{order.product}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>{order.mediatorName || 'N/A'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: Number(order.penaltyAmount) > 0 ? '#dc2626' : '#64748b', fontWeight: Number(order.penaltyAmount) > 0 ? 700 : 400 }}>
                        {Number(order.penaltyAmount) > 0 ? `₹${Number(order.penaltyAmount).toLocaleString()}` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>₹{order.amount}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', backgroundColor: getStatusBadge(order.status).bg, color: getStatusBadge(order.status).color }}>{getStatusBadge(order.status).label}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#64748b' }}>{order.refundFormFilledDate || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleViewOrder(order)}
                            style={{
                              padding: '6px 12px',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                          >
                            <Icon name="eye" size={14} />
                            View
                          </button>
                          <button
                            onClick={() => handleWhatsAppClick(order.whatsappNumber, order)}
                            style={{ padding: '6px 10px', background: '#25D366', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                            title="Contact Buyer"
                          >
                            Buyer
                          </button>
                          <button
                            onClick={() => handleWhatsAppClick(order.mediatorPhoneNumber)}
                            style={{ padding: '6px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                            title="Contact Mediator"
                          >
                            Mediator
                          </button>
                          <button
                            onClick={() => handleEditOrder(order)}
                            style={{
                              padding: '6px 12px',
                              background: '#f1f5f9',
                              color: '#475569',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title="Edit order"
                          >
                            <Icon name="edit" size={14} />
                            Edit
                          </button>
                          <button
                            onClick={() => openStatusChangeModal(order)}
                            disabled={updatingStatus === order.id}
                            style={{
                              padding: '6px 12px',
                              background: '#7c3aed',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: updatingStatus === order.id ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              opacity: updatingStatus === order.id ? 0.6 : 1,
                            }}
                            title="Change order status"
                          >
                            <Icon name="refresh" size={14} />
                            Status
                          </button>
                          <button
                            onClick={() => openDeleteModal(order.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title="Delete order"
                          >
                            <Icon name="x" size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationComponent pagination={pagination} onPageChange={fetchOrders} />
        </div>
      </div>

      {statusChangeOrder && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
            padding: '16px',
          }}
          onClick={closeStatusChangeModal}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>Change Order Status</h3>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>Select a new status for this order</p>
              </div>
              <button
                type="button"
                onClick={closeStatusChangeModal}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icon name="x" size={18} />
              </button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Order</div>
                <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                  <div><span style={{ color: '#64748b' }}>Order ID: </span><strong>{statusChangeOrder.orderId}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Customer: </span><strong>{statusChangeOrder.customer}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Product: </span><strong>{statusChangeOrder.product}</strong></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ color: '#64748b' }}>Current status:</span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: '700',
                      backgroundColor: getStatusBadge(statusChangeOrder.status).bg,
                      color: getStatusBadge(statusChangeOrder.status).color,
                    }}>
                      {getStatusBadge(statusChangeOrder.status).label}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>
                Select new status
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                {orderStatuses.map((status) => {
                  const isCurrent = statusChangeOrder.status === status.value;
                  const badge = getStatusBadge(status.value);
                  return (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => !isCurrent && handleStatusUpdateClick(statusChangeOrder.id, status.value)}
                      disabled={isCurrent || updatingStatus === statusChangeOrder.id}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: isCurrent ? `2px solid ${badge.color}` : '1px solid #e2e8f0',
                        background: isCurrent ? badge.bg : '#ffffff',
                        cursor: isCurrent || updatingStatus === statusChangeOrder.id ? 'default' : 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.15s ease',
                        opacity: isCurrent ? 1 : 0.95,
                      }}
                      onMouseEnter={(e) => {
                        if (!isCurrent && updatingStatus !== statusChangeOrder.id) {
                          e.currentTarget.style.borderColor = badge.color;
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrent) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: badge.color,
                        flexShrink: 0,
                        boxShadow: `0 0 0 3px ${badge.bg}`,
                      }} />
                      <span style={{ fontSize: '14px', fontWeight: isCurrent ? '700' : '500', color: isCurrent ? badge.color : '#334155', flex: 1 }}>
                        {status.label}
                      </span>
                      {isCurrent && <Icon name="check" size={16} style={{ color: badge.color }} />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={closeStatusChangeModal}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showBulkStatusModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
            padding: '16px',
          }}
          onClick={() => setShowBulkStatusModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>Bulk Status Update</h3>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Update <strong>{selectedOrderIds.length}</strong> selected order{selectedOrderIds.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkStatusModal(false)}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icon name="x" size={18} />
              </button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>
                Select status to apply
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                {bulkStatuses.map((status) => {
                  const badge = getStatusBadge(status.value);
                  return (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => handleBulkMarkClick(status.value)}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        background: '#ffffff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = badge.color;
                        e.currentTarget.style.background = badge.bg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.background = '#ffffff';
                      }}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: badge.color,
                        flexShrink: 0,
                        boxShadow: `0 0 0 3px ${badge.bg}`,
                      }} />
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>{status.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowBulkStatusModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showReasonModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '450px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>Reason for {getStatusBadge(pendingStatusUpdate?.newStatus).label}</h3>
            <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>Please provide a reason for this status change. This will be visible to the buyer.</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason here..."
              style={{ width: '100%', minHeight: '120px', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', fontSize: '14px', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => { setShowReasonModal(false); setPendingStatusUpdate(null); }}>Cancel</Button>
              <Button onClick={confirmReasonUpdate}>Update Status</Button>
            </div>
          </div>
        </div>
      )}

      {showOrderModal && selectedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Order Details</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Reference: {selectedOrder.orderId}</p>
              </div>
              <button onClick={handleCloseModal} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer' }}><Icon name="x" size={20} /></button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Basic Info</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Date:</span><strong>{selectedOrder.date}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Amount:</span><strong style={{ color: '#059669', fontSize: '15px' }}>₹{selectedOrder.amount}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Status:</span><strong style={{ color: getStatusBadge(selectedOrder.status).color }}>{getStatusBadge(selectedOrder.status).label}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Refund Filled:</span><strong>{selectedOrder.refundFormFilledDate || 'MISSED / —'}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Penalty:</span><strong style={{ color: Number(selectedOrder.penaltyAmount) > 0 ? '#dc2626' : '#059669' }}>{Number(selectedOrder.penaltyAmount) > 0 ? `₹${Number(selectedOrder.penaltyAmount).toLocaleString()} Applied` : 'No Penalty'}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Order ID:</span><strong>{selectedOrder.orderId}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Customer</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Name: <strong>{selectedOrder.customer}</strong></div>
                    <div>Email: <strong>{selectedOrder.userEmail}</strong></div>
                    <div>WhatsApp: <strong>{selectedOrder.whatsappNumber}</strong></div>
                    <div>Phone: <strong>{selectedOrder.phoneNumber || 'N/A'}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Product</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Name: <strong>{selectedOrder.product}</strong></div>
                    <div>Brand: <strong>{selectedOrder.brandName || 'N/A'}</strong></div>
                    <div>Platform: <strong>{selectedOrder.platformName || 'N/A'}</strong></div>
                    <div>Code: <strong>{selectedOrder.productCode || 'N/A'}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Bank Info</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div>Bank: <strong>{selectedOrder.bankName || 'N/A'}</strong></div>
                    <div>A/C No: <strong>{selectedOrder.bankAccount || 'N/A'}</strong></div>
                    <div>UPI: <strong>{selectedOrder.upiId || 'N/A'}</strong></div>
                    <div>Payment: <strong>{selectedOrder.paymentMethod || 'N/A'}</strong></div>
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px', gridColumn: '1 / -1' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Additional Information</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Mediator:</span>
                      <div style={{ fontWeight: '600', marginTop: '2px' }}>{selectedOrder.mediatorName || 'N/A'}</div>
                    </div>

                    <div>
                      <span style={{ color: '#6b7280' }}>Order Type:</span>
                      <div style={{ fontWeight: '600', marginTop: '2px' }}>{selectedOrder.dealOrderType || '-'}</div>
                    </div>

                    {selectedOrder.reason && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ color: '#6b7280' }}>Reason:</span>
                        <div style={{ fontWeight: '600', marginTop: '2px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                          {selectedOrder.reason}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Screenshots Section */}
                {(selectedOrder.sellerFeedbackScreenshot || selectedOrder.deliveredOrderScreenshot ||
                  selectedOrder.returnWindowCloseScreenshot || selectedOrder.reviewRatingScreenshot ||
                  selectedOrder.orderScreenshot) && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px', gridColumn: '1 / -1' }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Screenshots & Documents</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>

                      {selectedOrder.orderScreenshot && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                              <Icon name="file-text" size={16} style={{ color: '#6b7280' }} />
                              Order Screenshot
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: '#ffffff' }}>
                            <img
                              src={selectedOrder.orderScreenshot}
                              alt="Order Screenshot"
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => window.open(selectedOrder.orderScreenshot, '_blank')}
                            />
                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => window.open(selectedOrder.orderScreenshot, '_blank')}
                                style={{
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedOrder.deliveredOrderScreenshot && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                              <Icon name="package" size={16} style={{ color: '#10b981' }} />
                              Delivered Order Screenshot
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: '#ffffff' }}>
                            <img
                              src={selectedOrder.deliveredOrderScreenshot}
                              alt="Delivered Order Screenshot"
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => window.open(selectedOrder.deliveredOrderScreenshot, '_blank')}
                            />
                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => window.open(selectedOrder.deliveredOrderScreenshot, '_blank')}
                                style={{
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedOrder.returnWindowCloseScreenshot && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                              <Icon name="clock" size={16} style={{ color: '#f59e0b' }} />
                              Return Window Close Screenshot
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: '#ffffff' }}>
                            <img
                              src={selectedOrder.returnWindowCloseScreenshot}
                              alt="Return Window Close Screenshot"
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => window.open(selectedOrder.returnWindowCloseScreenshot, '_blank')}
                            />
                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => window.open(selectedOrder.returnWindowCloseScreenshot, '_blank')}
                                style={{
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedOrder.reviewRatingScreenshot && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                              <Icon name="star" size={16} style={{ color: '#f59e0b' }} />
                              Review Rating Screenshot
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: '#ffffff' }}>
                            <img
                              src={selectedOrder.reviewRatingScreenshot}
                              alt="Review Rating Screenshot"
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => window.open(selectedOrder.reviewRatingScreenshot, '_blank')}
                            />
                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => window.open(selectedOrder.reviewRatingScreenshot, '_blank')}
                                style={{
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedOrder.sellerFeedbackScreenshot && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ padding: '12px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                              <Icon name="message-circle" size={16} style={{ color: '#8b5cf6' }} />
                              Seller Feedback Screenshot
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: '#ffffff' }}>
                            <img
                              src={selectedOrder.sellerFeedbackScreenshot}
                              alt="Seller Feedback Screenshot"
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => window.open(selectedOrder.sellerFeedbackScreenshot, '_blank')}
                            />
                            <div style={{ marginTop: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => window.open(selectedOrder.sellerFeedbackScreenshot, '_blank')}
                                style={{
                                  fontSize: '12px',
                                  color: '#3b82f6',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                )}
                {selectedOrder.notes && (
                  <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px', gridColumn: '1 / -1' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', textTransform: 'uppercase' }}>Notes</h4>
                    <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#374151' }}>{selectedOrder.notes}</div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
            </div>
          </div>
        </div>
      )}
      {showEditModal && editingOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Edit Order: {editingOrder.orderId}</h3>
              <button onClick={() => setShowEditModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="x" size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Reviewer Name</label>
                  <input
                    type="text"
                    value={editFormData.reviewerName || ''}
                    onChange={(e) => setEditFormData({...editFormData, reviewerName: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Mediator</label>
                  <SearchableSelect
                    options={mediatorOptions}
                    value={editFormData.mediatorId || ''}
                    onChange={(value) => setEditFormData({ ...editFormData, mediatorId: value })}
                    placeholder="Select mediator"
                    searchPlaceholder="Search mediator..."
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Order Status</label>
                  <SearchableSelect
                    options={orderStatusOptions}
                    value={editFormData.orderStatus || ''}
                    onChange={(value) => setEditFormData({ ...editFormData, orderStatus: value })}
                    placeholder="Select status"
                    searchPlaceholder="Search status..."
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>WhatsApp Number</label>
                  <input
                    type="text"
                    value={editFormData.whatsappNumber || ''}
                    onChange={(e) => setEditFormData({...editFormData, whatsappNumber: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Order Amount</label>
                  <input
                    type="number"
                    value={editFormData.orderAmount || ''}
                    onChange={(e) => setEditFormData({...editFormData, orderAmount: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Review Link</label>
                  <input
                    type="text"
                    value={editFormData.reviewLink || ''}
                    onChange={(e) => setEditFormData({...editFormData, reviewLink: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>UPI ID</label>
                  <input
                    type="text"
                    value={editFormData.upiId || ''}
                    onChange={(e) => setEditFormData({...editFormData, upiId: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Bank Name</label>
                  <input
                    type="text"
                    value={editFormData.bankName || ''}
                    onChange={(e) => setEditFormData({...editFormData, bankName: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Account Number</label>
                  <input
                    type="text"
                    value={editFormData.bankAccount || ''}
                    onChange={(e) => setEditFormData({...editFormData, bankAccount: e.target.value})}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Reason (Optional)</label>
                  <textarea
                    value={editFormData.reason || ''}
                    onChange={(e) => setEditFormData({...editFormData, reason: e.target.value})}
                    style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                </div>
              </div>
              <div style={{ padding: '24px 0 0 0', borderTop: '1px solid #e5e7eb', marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button variant="secondary" type="button" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button type="submit" loading={loading}>Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* Status Update Confirmation Modal */}
      {showStatusConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '400px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              color: '#d97706'
            }}>
              <Icon name="alert-triangle" size={24} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>Confirm Status Change</h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: '1.5' }}>
              Are you sure you want to change the status of this order to <strong>{orderStatuses.find(s => s.value === statusToUpdate?.newStatus)?.label}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                variant="secondary"
                onClick={() => setShowStatusConfirm(false)}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmStatusUpdate}
                loading={updatingStatus !== null}
                style={{ flex: 1, background: '#2563eb' }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
      {showBulkConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '12px',
              width: '400px'
            }}>
              <h3 style={{ marginBottom: '12px' }}>Confirm Bulk Update</h3>

              <p style={{ fontSize: '14px', color: '#475569' }}>
                Are you sure you want to update {selectedOrderIds.length} orders to{' '}
                <strong>{getStatusBadge(bulkTargetStatus).label}</strong>?
              </p>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <Button variant="secondary" onClick={() => setShowBulkConfirm(false)}>
                  Cancel
                </Button>

                <Button onClick={confirmBulkMark} loading={isBulkMarking}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
      )}
      {deleteModal.open && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Delete Product</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                  Are you sure you want to delete? This action cannot be undone.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <Button
                    variant="secondary"
                    onClick={() => {
                      setDeleteModal(false);
                    }}
                >
                  Cancel
                </Button>
                <Button
                    onClick={confirmDelete}
                    style={{ background: '#ef4444', borderColor: '#ef4444' }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
      )}
    </div>

  );
};

export default AdminOrders;
